import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { generateInvoices, type GenerateInvoicesResult } from "../api/invoices";
import { InvoicesSection } from "../components/InvoicesSection";
import { periodOptions } from "../components/BillingStatsView";

export function InvoicesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Facturas
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Cobro mensual del fee a comercios. Se genera por período (YYYY-MM).
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Generar facturas
        </Button>
      </Stack>

      <InvoicesSection key={reloadKey} showMerchantColumn />

      <GenerateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onDone={() => {
          setDialogOpen(false);
          setReloadKey((k) => k + 1);
        }}
      />
    </Stack>
  );
}

function GenerateDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const options = periodOptions(6);
  const [period, setPeriod] = useState(options[1]?.value ?? options[0]?.value ?? "");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateInvoicesResult | null>(null);

  const submit = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await generateInvoices({ period });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error generando facturas");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generar facturas</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Se genera una factura por comercio activo con plan vigente. Si ya existe
            la factura del período, se skipea (idempotente).
          </Alert>

          <TextField
            select
            label="Período a facturar"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            fullWidth
          >
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {error && <Alert severity="error">{error}</Alert>}

          {result && (
            <Stack spacing={1}>
              <Alert severity="success">
                Se generaron {result.generated?.length ?? 0} facturas. Skipeados:{" "}
                {result.skipped?.length ?? 0}.
              </Alert>
              {(result.skipped?.length ?? 0) > 0 && (
                <Box sx={{ maxHeight: 180, overflow: "auto" }}>
                  {(result.skipped ?? []).map((s, idx) => (
                    <Typography
                      key={`${s.merchantId}-${idx}`}
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      <code>{s.merchantId}</code> — {s.reason}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={onDone}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={running}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={submit} disabled={running}>
              {running ? "Generando…" : "Generar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
